import { Component, h } from 'preact'
import ProductUpdateView from './ProductUpdateView'
import { ProductUpdateType } from './Types'
import qs from 'qs'
import Axios from 'axios'
import FilterPill from '../filter-pill/FilterPill'
import Phrase from '../../libs/translationsUtils/phrase'
import { LikedUserType } from '../topic-list-view/LikedUsers'

interface Props {
  isGuest: boolean
  productUpdates: ProductUpdateType[]
  productAreas: Array<{
    id: string
    name: string
  }>
  currentPage: string
  totalPageCount: number
  totalTopicCount: number
  currentUser?: LikedUserType
}

type filterTypes = null | {
  productArea?: string[]
}

interface State {
  productUpdates: ProductUpdateType[]
  isLoading: boolean
  currentFilters: filterTypes
  currentPage: number
  totalPageCount: number
  totalTopicCount: number
}

class ProductUpdatesOverview extends Component<Props, State> {
  isPageNumberIncreased: boolean = false
  showBackButton: boolean = false
  productAreaMapping: {
    [key: string]: string
  } = {}

  productAreaReverseMapping: {
    [key: string]: string
  } = {}

  filtersList = ['productArea']

  constructor (props: Props) {
    super(props)
    const params = this.prepareParametersForRequest()

    this.state = {
      isLoading: false,
      productUpdates: props.productUpdates,
      currentFilters: params.filters,
      currentPage: parseInt(props.currentPage),
      totalPageCount: props.totalPageCount,
      totalTopicCount: props.totalTopicCount
    }

    if (props.productAreas?.length) {
      (props.productAreas || []).forEach(productArea => {
        this.productAreaMapping[productArea.name] = productArea.id
        this.productAreaReverseMapping[productArea.id] = productArea.name
      })
    }

    this.showBackButton = parseInt(props.currentPage) !== 1

    this.setQueryParams = this.setQueryParams.bind(this)
    this.getNewTopicsList = this.getNewTopicsList.bind(this)
    this.prepareParametersForRequest = this.prepareParametersForRequest.bind(this)
    this.updateFilters = this.updateFilters.bind(this)
    this.setPagination = this.setPagination.bind(this)
  }

  setQueryParams (newQuery: { page?: number, filters?: Object }) {
    const searchQuery = window.location.search
    const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })

    const combinedQuery = { ...queryParameters, ...newQuery }

    if (combinedQuery?.page === 1) {
      delete combinedQuery?.page
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    window.history.pushState('', '', `?${qs.stringify(combinedQuery)}`)
  }

  setPagination (newPage: number) {
    if (newPage > this.state.currentPage) {
      this.isPageNumberIncreased = true
    }
    this.setState({
      currentPage: newPage
    }, () => this.setQueryParams({ page: this.state.currentPage }))
  }

  async getNewTopicsList () {
    const params = this.prepareParametersForRequest()

    this.setState({ isLoading: true })

    try {
      const res = await Axios.get('/productUpdates/topics', {
        params
      })

      if (res.data) {
        // Hide show back button if filters are changing except pagination
        if (!this.isPageNumberIncreased) {
          this.showBackButton = false
        }

        this.setState({
          productUpdates: this.isPageNumberIncreased ? this.state.productUpdates.concat(res.data.topics) : res.data.topics,
          currentPage: this.isPageNumberIncreased ? this.state.currentPage : 1,
          totalPageCount: res.data.totalPageCount,
          totalTopicCount: res.data.totalTopicCount,
          isLoading: false,
          currentFilters: params.filters
        })
        this.isPageNumberIncreased = false
      }
    } catch {
      this.isPageNumberIncreased = false
      this.setState({ isLoading: false })
    }
  }

  prepareParametersForRequest () {
    const searchQuery = typeof window !== 'undefined' ? window.location.search : ''
    const queryParameters = qs.parse(searchQuery, { ignoreQueryPrefix: true })
    const filters = queryParameters.filters || {}

    if (filters.productArea) {
      filters.productArea = filters.productArea.map((productAreaName: string) => this.productAreaMapping[productAreaName])
    }

    let filterKeys = Object.keys(filters)
    filterKeys = filterKeys.filter(key => filters[key] !== null)

    const requestFilters = filterKeys.reduce((acc: {[key: string]: Array<string | null>}, newFilter) => {
      acc[newFilter] = filters[newFilter]
      return acc
    }, {})

    return {
      page: queryParameters.page || 1,
      filters: requestFilters
    }
  }

  updateFilters (filter: string) {
    if (filter === 'productArea') {
      window.dispatchEvent(new CustomEvent('productAreaFilterRemoval'))
    }
  }

  handleBackNavigation () {
    window.location.reload()
  }

  componentDidMount () {
    if (typeof window === 'undefined') {
      return
    }
    (function (history) {
      const pushState = history.pushState
      window.history.pushState = function (state) {
        if (typeof window.history.onpushstate === 'function') {
          window.history.onpushstate({
            state: state
          })
        }
        // @ts-expect-error
        return pushState.apply(history, arguments)
      }
    })(window.history)
    window.history.onpushstate = () => {
      // pushstate is async event, without setTimeout it is getting previous state
      window.setTimeout(async () => await this.getNewTopicsList(), 0)
    }
    window.addEventListener('popstate', this.handleBackNavigation)
  }

  componentWillUnmount () {
    if (typeof window === 'undefined') {
      return
    }
    window.removeEventListener('popstate', this.handleBackNavigation)
  }

  render ({ isGuest, currentUser }: Props, { productUpdates, isLoading, totalPageCount, currentPage, totalTopicCount }: State) {
    const searchQuery = typeof window !== 'undefined' ? window.location.search : ''
    const filters = qs.parse(searchQuery, { ignoreQueryPrefix: true }).filters || {}
    const filterKeys: string[] = Object.keys(filters).filter(filter => this.filtersList.includes(filter))

    if (typeof window !== 'undefined') {
      if (filterKeys.length) {
        document.body.classList.add('filters-applied')
      } else {
        document.body.classList.remove('filters-applied')
      }
    }
    return (
      <div className='product-updates-overview-wrapper'>
        <div className='product-updates-overview'>
          {
            filterKeys.length
              ? (
                <ul className='filter-list is-hidden-L'>
                  {
                  filterKeys.map(filter => (
                    <FilterPill
                      key={filters[filter][0]}
                      name={filters[filter][0]}
                      onClick={() => this.updateFilters(filter)}
                      isDismissable
                    />
                  ))
                }
                </ul>
                )
              : null
          }
          {
            filterKeys.length
              ? (
                <header className='product-updates-list-header'>
                  <h3 className='list__items--header-heading qa-thread-list-count'>
                    <Phrase p='product.updates.counter' count={{ n: totalTopicCount.toString() }} />
                  </h3>
                </header>
                )
              : null
          }
          {
            this.showBackButton
              ? (
                <div className='load-more-container back-to-top-button card-widget-wrapper'>
                  <button
                    className={`btn--cta btn--full-width btn--load-more ${isLoading ? 'is-loading disabled' : ''}`}
                    onClick={() => this.setPagination(1)} type='button'
                  >
                    Back to top of the list
                    <span className='loader' />
                  </button>
                </div>
                )
              : null
          }
          {
            productUpdates.map(productUpdate => (
              <ProductUpdateView
                key={productUpdate.id}
                productAreasMapping={this.productAreaReverseMapping}
                {...productUpdate}
                isGuest={isGuest}
                currentUser={currentUser}
                likeSet={productUpdate.likeSet ?? []}
                lastContributor={productUpdate.lastContributor}
                lastPostId={productUpdate.lastPostId}
              />
            ))
          }
          {
            currentPage < totalPageCount
              ? (
                <div className='load-more-container card-widget-wrapper'>
                  <button
                    className={`btn--cta btn--full-width btn--load-more ${isLoading ? 'is-loading disabled' : ''}`}
                    onClick={() => this.setPagination(currentPage + 1)} type='button'
                  >
                    <Phrase p='Show more' />
                    <span className='loader' />
                    <a href={`/product-updates?page=${currentPage + 1}`} />
                  </button>
                </div>
                )
              : null
          }
        </div>
      </div>
    )
  }
}

export default ProductUpdatesOverview
